import { useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { COUNTRIES, countryName, flagEmoji } from "../i18n/countries";

export type CountryOption = { code: string; label: string };

type Props = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  /** Applied to the wrapper — use for width / margins. */
  style?: CSSProperties;
};

/** Strips diacritics and lowercases, so "svaj" also matches "Švajcarska". */
function normalize(s: string): string {
  return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

/** Pure filter — case/diacritic-insensitive substring match against `label`. Exported for unit testing. */
export function filterCountryOptions(options: CountryOption[], query: string): CountryOption[] {
  const q = normalize(query.trim());
  if (!q) return options;
  return options.filter((o) => normalize(o.label).includes(q));
}

/**
 * Searchable, localized country combobox — a `Select`-styled sibling for the
 * one field a plain fixed-option `Select` can't serve well: ~195 countries is
 * too many to scan without a filter. Extends Select.tsx's visual language
 * (same trigger/panel/option tokens) with a search input pinned atop the
 * list. Options are `countryName(code, locale)` (Intl.DisplayNames) so the
 * list — and the filter text users type — is in the app's locale, not
 * English. Always offers an empty "—" row (pinned above the filtered list)
 * so a country can be cleared even mid-search.
 */
export function CountrySelect({ value, onChange, disabled, placeholder, ariaLabel, style }: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const options = useMemo<CountryOption[]>(
    () => COUNTRIES
      .map((c) => ({ code: c.code, label: countryName(c.code, locale) }))
      .sort((a, b) => a.label.localeCompare(b.label, locale)),
    [locale],
  );
  const filtered = useMemo(() => filterCountryOptions(options, query), [options, query]);
  // Index 0 is always the pinned "—" (clear) row; 1..n are `filtered`.
  const rowCount = filtered.length + 1;

  const selected = options.find((o) => o.code === value) ?? null;

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActive(value ? Math.max(0, filtered.findIndex((o) => o.code === value) + 1) : 0);
    const id = requestAnimationFrame(() => searchRef.current?.focus());
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  function choose(code: string) {
    onChange(code);
    setOpen(false);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActive((a) => Math.min(rowCount - 1, a + 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActive((a) => Math.max(0, a - 1));
        break;
      case "Enter":
        e.preventDefault();
        if (active === 0) choose("");
        else { const opt = filtered[active - 1]; if (opt) choose(opt.code); }
        break;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        break;
    }
  }

  return (
    <div ref={wrapRef} style={{ position: "relative", ...style }}>
      <button
        type="button"
        className="input"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => { if (!disabled) setOpen((o) => !o); }}
        style={trigger}
      >
        <span style={{ ...triggerLabel, color: selected ? "var(--ink)" : "var(--ink-3)" }}>
          {selected ? (
            <>
              <span aria-hidden>{flagEmoji(selected.code)}</span>
              {selected.label}
            </>
          ) : (placeholder ?? "—")}
        </span>
        <svg width="10" height="6" viewBox="0 0 10 6" aria-hidden
          style={{ flex: "none", transform: open ? "rotate(180deg)" : "none", transition: "transform .12s", color: "var(--ink-3)" }}>
          <path fill="none" stroke="currentColor" strokeWidth="1.5" d="M1 1l4 4 4-4" />
        </svg>
      </button>

      {open && (
        <div style={panel}>
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t("action.search", "Search") + "…"}
            aria-label={t("action.search", "Search")}
            className="input"
            style={searchInput}
          />
          <div role="listbox" aria-label={ariaLabel} className="dd-scroll" style={list}>
            <button
              type="button"
              role="option"
              aria-selected={value === ""}
              onMouseEnter={() => setActive(0)}
              onClick={() => choose("")}
              style={{ ...option, background: active === 0 ? "var(--surface-2)" : "transparent", fontWeight: value === "" ? 600 : 400 }}
            >
              <span style={{ flex: 1, color: "var(--ink-3)" }}>—</span>
            </button>

            {filtered.length === 0 ? (
              <div style={emptyMsg}>{t("search.noResults", { term: query, defaultValue: 'No results for "{{term}}"' })}</div>
            ) : (
              filtered.map((o, i) => {
                const rowIndex = i + 1;
                const isSel = o.code === value;
                return (
                  <button
                    key={o.code}
                    type="button"
                    role="option"
                    aria-selected={isSel}
                    onMouseEnter={() => setActive(rowIndex)}
                    onClick={() => choose(o.code)}
                    style={{ ...option, background: rowIndex === active ? "var(--surface-2)" : "transparent", fontWeight: isSel ? 600 : 400 }}
                  >
                    <span aria-hidden style={{ width: 20, flex: "none" }}>{flagEmoji(o.code)}</span>
                    <span style={{ flex: 1 }}>{o.label}</span>
                    {isSel && (
                      <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden style={{ flex: "none", color: "var(--brand-primary)" }}>
                        <path fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" d="M5 12l5 5L20 7" />
                      </svg>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const trigger: CSSProperties = {
  display: "flex", alignItems: "center", gap: 8, textAlign: "left", cursor: "pointer",
};
const triggerLabel: CSSProperties = {
  flex: 1, display: "flex", alignItems: "center", gap: 8,
  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};
const panel: CSSProperties = {
  position: "absolute", zIndex: 30, top: "calc(100% + 4px)", left: 0, right: 0,
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: "var(--r-sm)", boxShadow: "var(--shadow-2)",
  padding: 8, display: "flex", flexDirection: "column", gap: 6,
};
const searchInput: CSSProperties = { flex: "none" };
const list: CSSProperties = { maxHeight: 280, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 };
const option: CSSProperties = {
  display: "flex", gap: 10, alignItems: "center", width: "100%", textAlign: "left",
  padding: "8px 10px", borderRadius: 6, border: "none", cursor: "pointer",
  fontSize: 13, color: "var(--ink)",
};
const emptyMsg: CSSProperties = { padding: "10px 10px", fontSize: 12.5, color: "var(--ink-3)" };
